// Panel Size statistics — the population universe behind every penetration
// number, and how the chosen category's users sit inside it per demographic.
import { useState, useMemo } from 'react';
import { useMetaData, useApi } from '../hooks/useApi';
import { useCategory } from '../context/CategoryContext';
import { useMonth } from '../context/MonthContext';
import { Panel } from '../utils/api';
import { LineChart, BarChart, ChartCard, ChartLoader, ChartError } from '../components/Charts';
import { COLORS, AGE_ORDER, fmtNum, fmtMonth, fmtPct } from '../utils/theme';
import { geoName } from '../utils/postcodes';
import { UK_CENSUS_AGE_PCT, UK_CENSUS_GENDER_PCT, UK_CENSUS_GEO_PCT, CENSUS_SOURCE } from '../utils/census';

const GENDER_LABEL = { M: 'Male', F: 'Female', U: 'Unknown' };
const TOP_GEOS = 20;

// Returns absolute two-series config (panel universe + cat users)
function absSeries(rows, catLabel) {
  return [
    { name: 'Panel Universe', color: COLORS.indigo, data: rows.map(r => r.universe) },
    { name: `${catLabel} Users`, color: COLORS.teal, data: rows.map(r => r.cat_users ?? 0) },
  ];
}

// Compute index rows: panel % and cat-users % each indexed vs census
function toIndexRows(segRows, censusPct, totalUniverse, totalCatUsers) {
  if (!totalUniverse || !totalCatUsers) return [];
  return segRows.map(r => {
    const cPct = censusPct[r.attribute_value];
    if (!cPct) return null;
    const pShare = (r.universe / totalUniverse) * 100;
    const cShare = ((r.cat_users ?? 0) / totalCatUsers) * 100;
    return {
      label:    r.attribute_value,
      panelIdx: Math.round((pShare / cPct) * 100),
      catIdx:   Math.round((cShare / cPct) * 100),
    };
  }).filter(Boolean);
}

function idxSeries(idxRows, catLabel) {
  return [
    { name: 'Panel Index', color: COLORS.indigo, data: idxRows.map(r => r.panelIdx) },
    { name: `${catLabel} Index`, color: COLORS.teal, data: idxRows.map(r => r.catIdx) },
  ];
}

const INDEX_SUB = 'Index 100 = census parity · >100 over-represented · <100 under-represented';

function ToggleBtn({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        height: 28,
        padding: '0 10px',
        fontSize: 11,
        fontWeight: 500,
        fontFamily: 'inherit',
        border: '1px solid',
        borderColor: active ? 'var(--teal)' : 'var(--border-mid)',
        background: active ? 'var(--teal)' : 'var(--bg-card)',
        color: active ? '#fff' : 'var(--text-secondary)',
        borderRadius: 'var(--radius-sm)',
        cursor: 'pointer',
        transition: 'all 0.13s',
      }}
    >
      {label}
    </button>
  );
}

export default function PanelSizeView() {
  const { category } = useCategory();
  const { month }    = useMonth();
  const { sessionTypes, latestMonth, loading: metaLoading } = useMetaData();

  const [sessionType, setSessionType] = useState('open_app');
  const [indexMode,   setIndexMode]   = useState(true);

  const effectiveCat  = category || 'ALL';
  const selectedMonth = month || latestMonth;
  const p             = { category: effectiveCat, session_type: sessionType };

  const overview = useApi(
    () => Panel.overview({ ...p, month_id: selectedMonth }),
    { effectiveCat, sessionType, selectedMonth },
    !!selectedMonth,
  );
  const trend = useApi(() => Panel.trend(p), { effectiveCat, sessionType });

  const rows    = overview.data || [];
  const overall = rows.find(r => r.attribute === 'overall');

  const ageRows = useMemo(() => {
    const bands = rows.filter(r => r.attribute === 'age_band');
    return AGE_ORDER.filter(b => bands.some(r => r.attribute_value === b))
      .map(b => bands.find(r => r.attribute_value === b))
      .concat(bands.filter(r => !AGE_ORDER.includes(r.attribute_value)));
  }, [rows]);

  const genderRows = useMemo(() => rows.filter(r => r.attribute === 'gender'), [rows]);
  const geoRows    = useMemo(
    () => rows
      .filter(r => r.attribute === 'postcode_area' && r.attribute_value !== 'BT' && r.attribute_value !== 'Unknown')
      .slice(0, TOP_GEOS),
    [rows],
  );

  const totalUniverse = overall?.universe  ?? 0;
  const totalCatUsers = overall?.cat_users ?? 0;

  const ageIdxRows = useMemo(
    () => toIndexRows(ageRows, UK_CENSUS_AGE_PCT, totalUniverse, totalCatUsers),
    [ageRows, totalUniverse, totalCatUsers],
  );
  const genderIdxRows = useMemo(
    () => toIndexRows(genderRows, UK_CENSUS_GENDER_PCT, totalUniverse, totalCatUsers),
    [genderRows, totalUniverse, totalCatUsers],
  );
  const geoIdxRows = useMemo(() => {
    const idxRows = toIndexRows(
      geoRows.filter(r => UK_CENSUS_GEO_PCT[r.attribute_value] != null && r.attribute_value !== 'BT'),
      UK_CENSUS_GEO_PCT,
      totalUniverse,
      totalCatUsers,
    );
    return [...idxRows].sort((a, b) => b.panelIdx - a.panelIdx);
  }, [geoRows, totalUniverse, totalCatUsers]);

  const trendMonths = useMemo(() => (trend.data || []).map(r => r.month_id), [trend.data]);
  const trendSeries = useMemo(() => trend.data ? [
    { name: 'Panel Size (rolling 30d)', color: COLORS.indigo,    data: trend.data.map(r => r.panel_size ?? null) },
    { name: 'Avg Daily Actives',        color: COLORS.textMuted, data: trend.data.map(r => r.avg_daily_uniques ?? null) },
    { name: `${effectiveCat} Users`,    color: COLORS.teal,      data: trend.data.map(r => r.cat_users ?? null) },
  ] : [], [trend.data, effectiveCat]);

  if (metaLoading) return <div className="loading-state"><div className="spinner" /> Loading…</div>;

  const idxFmt = v => v;

  return (
    <div>
      <div className="filters-bar">
        <span className="filter-label">Session</span>
        <select className="filter-select" value={sessionType} onChange={e => setSessionType(e.target.value)}>
          {sessionTypes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <span className="filter-label" style={{ marginLeft: 'auto' }}>View</span>
        <div style={{ display: 'flex', gap: 4 }}>
          <ToggleBtn label="Index vs Census" active={indexMode}  onClick={() => setIndexMode(true)} />
          <ToggleBtn label="Absolute"        active={!indexMode} onClick={() => setIndexMode(false)} />
        </div>
      </div>

      <div className="trends-section-label">Panel Size — Total Population vs {effectiveCat}</div>

      {overview.loading && <ChartLoader height={120} />}
      {overview.error   && <ChartError message={overview.error} height={120} />}

      {!overview.loading && !overview.error && overall && (
        <div className="kpi-grid" style={{ marginBottom: 16 }}>
          <div className="kpi-card" style={{ background: COLORS.teal, borderColor: COLORS.teal }}>
            <div className="kpi-label" style={{ color: 'rgba(255,255,255,0.85)' }}>Total Panel Size</div>
            <div className="kpi-value" style={{ color: '#fff' }}>{fmtNum(overall.universe)}</div>
            <div className="kpi-delta neutral" style={{ color: 'rgba(255,255,255,0.75)' }}>
              rolling 30-day uniques · {fmtMonth(selectedMonth)}
            </div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Avg Daily Actives</div>
            <div className="kpi-value">{fmtNum(overall.avg_daily_uniques)}</div>
            <div className="kpi-delta neutral">average daily unique customers</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">{effectiveCat} Users</div>
            <div className="kpi-value">{fmtNum(overall.cat_users)}</div>
            <div className="kpi-delta neutral">'Any App' deduplicated · {sessionType}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Share of Panel</div>
            <div className="kpi-value">{fmtPct(overall.category_share_pct)}</div>
            <div className="kpi-delta neutral">category users ÷ panel size</div>
          </div>
        </div>
      )}

      <ChartCard
        title="Panel Size Trend — vs Category Users"
        sub={`Full history · panel is month-end rolling 30d · ${effectiveCat} · ${sessionType}`}
        style={{ marginBottom: 16 }}
      >
        {trend.loading && <ChartLoader height={300} />}
        {trend.error   && <ChartError message={trend.error} height={300} />}
        {!trend.loading && !trend.error && trendMonths.length > 0 && (
          <LineChart categories={trendMonths.map(fmtMonth)} series={trendSeries} height={300} />
        )}
      </ChartCard>

      {!overview.loading && !overview.error && rows.length > 0 && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <ChartCard
              title="By Age Band — Panel vs Category"
              sub={indexMode
                ? `${INDEX_SUB} · ${effectiveCat} · ${fmtMonth(selectedMonth)}`
                : `${effectiveCat} · ${fmtMonth(selectedMonth)}`}
            >
              {indexMode ? (
                <BarChart
                  categories={ageIdxRows.map(r => r.label)}
                  series={idxSeries(ageIdxRows, effectiveCat)}
                  height={300}
                  xFormatter={idxFmt}
                  markLines={[100]}
                />
              ) : (
                <BarChart
                  categories={ageRows.map(r => r.attribute_value)}
                  series={absSeries(ageRows, effectiveCat)}
                  height={300}
                />
              )}
            </ChartCard>

            <ChartCard
              title="By Gender — Panel vs Category"
              sub={indexMode
                ? `${INDEX_SUB} · ${effectiveCat} · ${fmtMonth(selectedMonth)}`
                : `${effectiveCat} · ${fmtMonth(selectedMonth)}`}
            >
              {indexMode ? (
                <BarChart
                  categories={genderIdxRows.map(r => GENDER_LABEL[r.label] || r.label)}
                  series={idxSeries(genderIdxRows, effectiveCat)}
                  height={300}
                  xFormatter={idxFmt}
                  markLines={[100]}
                />
              ) : (
                <BarChart
                  categories={genderRows.map(r => GENDER_LABEL[r.attribute_value] || r.attribute_value)}
                  series={absSeries(genderRows, effectiveCat)}
                  height={300}
                />
              )}
            </ChartCard>
          </div>

          <ChartCard
            title={indexMode
              ? `By Geography — Panel Index vs Census (Top ${geoIdxRows.length}, sorted by panel index)`
              : `By Geography — Panel vs Category (Top ${geoRows.length})`}
            sub={indexMode
              ? `${INDEX_SUB} · ${effectiveCat} · ${fmtMonth(selectedMonth)}`
              : `${effectiveCat} · ${fmtMonth(selectedMonth)} · largest panel universes first`}
          >
            {indexMode ? (
              <BarChart
                categories={geoIdxRows.map(r => geoName(r.label))}
                series={idxSeries(geoIdxRows, effectiveCat)}
                horizontal
                height={Math.max(360, geoIdxRows.length * 34 + 90)}
                xFormatter={idxFmt}
                markLines={[100]}
              />
            ) : (
              <BarChart
                categories={geoRows.map(r => geoName(r.attribute_value))}
                series={absSeries(geoRows, effectiveCat)}
                horizontal
                height={Math.max(360, geoRows.length * 34 + 90)}
              />
            )}
          </ChartCard>

          {indexMode && (
            <div style={{
              marginTop: 12,
              padding: '8px 12px',
              fontSize: 11,
              color: 'var(--text-muted)',
              borderTop: '1px solid var(--border)',
              lineHeight: 1.6,
            }}>
              <strong>Census reference:</strong> {CENSUS_SOURCE}
            <br />
            * Geography charts exclude <strong>Belfast (BT)</strong> — no panel coverage in Northern Ireland —
            and <strong>Unknown</strong> postcode areas where census attribution is not available.
            </div>
          )}
        </>
      )}
    </div>
  );
}
